import { NotificationLevel } from 'src/app/core/services/notification.service';

/** Nível visual usado nos componentes de UI */
export type NotificationViewLevel = 'success' | 'info' | 'warning' | 'error';

/** Configuração visual completa de uma notificação */
export interface NotificationStyleConfig {
    /** Nível visual mapeado */
    viewLevel: NotificationViewLevel;
    /** Ícone PrimeNG (pi pi-*) */
    primeIcon: string;
    /** Path SVG para ícones customizados */
    svgPath: string;
    /** Classe de cor do ícone */
    iconClass: string;
    /** Classe de cor do texto do header */
    headerClass: string;
    /** Classe de background */
    bgClass: string;
    /** Classe para badges inline */
    badgeClass: string;
    /** Chave de tradução para o tipo */
    titleKey: string;
}

/** Mapeamento centralizado de estilos por tipo de notificação */
const NOTIFICATION_STYLES: Record<NotificationLevel, NotificationStyleConfig> = {
    Error: {
        viewLevel: 'error',
        primeIcon: 'pi pi-times-circle',
        svgPath: 'M6 6l12 12M18 6l-12 12',
        iconClass: 'text-red-500',
        headerClass: 'text-red-700 dark:text-red-400',
        bgClass: 'bg-red-50 dark:bg-red-900/20',
        badgeClass: 'bg-rose-50 text-rose-700 ring-rose-600/20 dark:bg-rose-900/30 dark:text-rose-200',
        titleKey: 'shared.dialogs.notificationError.types.error'
    },
    Warning: {
        viewLevel: 'warning',
        primeIcon: 'pi pi-exclamation-triangle',
        svgPath: 'M12 9v4m0 4h.01',
        iconClass: 'text-amber-500',
        headerClass: 'text-amber-700 dark:text-amber-400',
        bgClass: 'bg-amber-50 dark:bg-amber-900/20',
        badgeClass: 'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-900/30 dark:text-amber-200',
        titleKey: 'shared.dialogs.notificationError.types.warning'
    },
    Information: {
        viewLevel: 'info',
        primeIcon: 'pi pi-info-circle',
        svgPath: 'M12 8h.01M11 12h2v4h-2',
        iconClass: 'text-blue-500',
        headerClass: 'text-blue-700 dark:text-blue-400',
        bgClass: 'bg-blue-50 dark:bg-blue-900/20',
        badgeClass: 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-900/30 dark:text-sky-200',
        titleKey: 'shared.dialogs.notificationError.types.info'
    },
    Promo: {
        viewLevel: 'success',
        primeIcon: 'pi pi-megaphone',
        svgPath: 'M5 13l4 4L19 7',
        iconClass: 'text-purple-500',
        headerClass: 'text-purple-700 dark:text-purple-400',
        bgClass: 'bg-purple-50 dark:bg-purple-900/20',
        badgeClass: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-900/30 dark:text-emerald-200',
        titleKey: 'shared.dialogs.notificationError.types.promo'
    }
};

/** Configuração padrão para tipos desconhecidos */
const DEFAULT_STYLE: NotificationStyleConfig = {
    viewLevel: 'info',
    primeIcon: 'pi pi-bell',
    svgPath: 'M12 8h.01M11 12h2v4h-2',
    iconClass: 'text-slate-500',
    headerClass: 'text-slate-700 dark:text-slate-400',
    bgClass: 'bg-slate-50 dark:bg-slate-900/20',
    badgeClass: 'bg-slate-50 text-slate-700 ring-slate-600/20 dark:bg-slate-900/30 dark:text-slate-200',
    titleKey: 'shared.dialogs.notificationError.types.default'
};

/**
 * Retorna a configuração de estilo completa para um tipo de notificação
 */
export function getNotificationStyle(type: NotificationLevel): NotificationStyleConfig {
    return NOTIFICATION_STYLES[type] ?? DEFAULT_STYLE;
}

/**
 * Mapeia o tipo de notificação do backend para o nível visual do frontend
 */
export function mapNotificationLevel(type: NotificationLevel): NotificationViewLevel {
    return getNotificationStyle(type).viewLevel;
}

/**
 * Retorna a classe CSS do badge para um tipo de notificação
 */
export function getNotificationBadgeClass(type: NotificationLevel): string {
    return getNotificationStyle(type).badgeClass;
}

/**
 * Retorna o path SVG do ícone para um tipo de notificação
 */
export function getNotificationIconPath(type: NotificationLevel): string {
    return getNotificationStyle(type).svgPath;
}

/**
 * Retorna a classe CSS do ícone para um tipo de notificação
 */
export function getNotificationIconClass(type: NotificationLevel): string {
    return getNotificationStyle(type).iconClass;
}
